import { useState } from "react";
import { Helmet } from "react-helmet-async";
import ContactUs from "../components/ContactUs";
import Footer from "../components/Footer";
import Header from "../components/Header";
import OptimizedImage from "../components/OptimizedImage";
import ResponsiveHeadshot from "../components/ResponsiveHeadshot";

export default function BiographyPage() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false); // Renamed for clarity

  const toggleContactModal = () => {
    setIsContactModalOpen(!isContactModalOpen);
  };

  return (
    <>
      <Helmet>
        <title>
          Certified Financial Coach Wendy Toh | 30+ Years Experience | Holly
          Springs NC
        </title>
        <meta
          name="description"
          content="Wendy Toh is a Certified Personal Financial Wellness Consultant with 30+ years IBM leadership experience. Helping individuals & couples nationwide achieve financial confidence and clarity."
        />
        <meta
          name="keywords"
          content="Wendy Toh, certified financial coach, personal financial wellness consultant, experienced financial advisor, money coach credentials, financial coaching expert to helpm with money, Holly Springs NC, North Carolina financial coach"
        />
        <link rel="canonical" href="https://www.ownyourmoney.biz/about" />

        <meta
          property="og:site_name"
          content="Own Your Money Financial Coaching"
        />
        <meta
          property="og:title"
          content="About Wendy Toh | Certified Financial Coach"
        />
        <meta property="og:url" content="https://www.ownyourmoney.biz/about" />
        <meta property="og:type" content="website" />
        <meta
          property="og:description"
          content="Meet Wendy Toh, Certified Personal Financial Wellness Coach serving clients in the Triangle North Carolina area. With over 30 years of leadership, she is passionate about advising individuals and couples to build confidence and clarity around their money."
        />
        <meta
          property="og:image"
          content="https://www.ownyourmoney.biz/og-image.png"
        />

        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="geo.region" content="US-NC" />
        <meta name="geo.country" content="United States" />
        <meta name="geo.placename" content="Holly Springs, NC" />
        <meta name="geo.position" content="35.65;-78.83" />
        <meta name="ICBM" content="35.65, -78.83" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="en-US" />
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://www.ownyourmoney.biz/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "About",
          "item": "https://www.ownyourmoney.biz/about"
        }
      ]
    }
    `}
        </script>

        {/* Combined FinancialService + Organization Schema */}
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "FinancialService",
      "name": "Own Your Money LLC",
      "url": "https://www.ownyourmoney.biz",
      "logo": "https://www.ownyourmoney.biz/static/media/logo.a289caf420b85a4e241ff32552050a01.svg",
      "image": "https://www.ownyourmoney.biz/og-image.png?v=2",
      "description": "Professional financial coaching and personal financial wellness services by Wendy Toh, helping clients across North Carolina and the United States.",
      "telephone": "+1-919-285-1689",
      "email": "OwnYourMoneyNC@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Holly Springs",
        "addressRegion": "NC",
        "addressCountry": "US"
      },
      "areaServed": [
        { "@type": "State", "name": "North Carolina" },
        { "@type": "Country", "name": "United States" }
      ],
      "founder": {
        "@type": "Person",
        "name": "Wendy Toh",
        "jobTitle": "Certified Personal Financial Wellness Consultant"
      },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+1-919-285-1689",
          "contactType": "customer service",
          "areaServed": ["US", "NC"],
          "availableLanguage": ["English"],
          "email": "OwnYourMoneyNC@gmail.com"
        }
      ],
      "sameAs": [
        "https://www.linkedin.com/in/wendytoh",
        "https://www.facebook.com/ownyourmoney"
      ],
      "serviceType": ["Financial Coaching", "Financial Consulting", "Budget Planning"],
      "priceRange": "$$"
    }
    `}
        </script>
      </Helmet>
      <Header />
      <section>
        <div className="bio-hero-row">
          <div className="bio-hero-col bio-hero-col-left">
            <h3 className="bio-title">Who is Wendy?</h3>
            <div className="bio-text">
              <p>
                Wendy Toh is a retired IT executive turned financial coach who
                is passionate about helping individuals and couples build
                confidence and clarity around their money. With over 30 years of
                leadership experience at IBM, Wendy brings a deep understanding
                of strategic thinking, problem-solving, and people development
                to her coaching practice. As the founder of Own Your Money,
                Wendy helps individuals and couples navigate life’s financial
                transitions with clarity, confidence, and emotional support.
                Wendy is a Certified Personal Financial Wellness Consultant.
              </p>
              <p>
                Originally from Malaysia, Wendy moved to the U.S. in the early
                1980s and earned her degree in Mathematics and Computer Science
                from the University of Florida. Her career at IBM spanned
                technical, managerial, and executive roles, where she was
                recognized for her mentorship, leadership, and commitment to
                diversity and inclusion.
              </p>

              <p>
                Now, Wendy is channeling her skills into financial
                wellness to empower others to take control of their financial
                lives with intention and purpose. Her coaching blends practical
                tools with empathetic listening and empowers clients to take
                control of their money by developing healthy financial
                behaviors. Whether you're rebuilding after a major change or
                seeking a more intentional approach to spending and saving,
                Wendy offers a safe, structured space to move forward.
              </p>
              <p>
                As an advocate for financial literacy, Wendy actively designs
                and delivers complimentary seminars and fee-based workshops to
                help individuals strengthen their financial knowledge and
                confidence.
              </p>

              <p>
                Wendy lives in North Carolina with her husband David. Together,
                they enjoy boating, traveling, watching football, and spending
                time with their friends and families.
              </p>
            </div>
          </div>
          <div className="bio-hero-col bio-hero-col-right">
            <div className="bio-image-frame">
              <ResponsiveHeadshot
                headshotNumber={2}
                alt="Wendy Toh"
                className="bio-headshot"
              />
            </div>
            <div className="bio-contact-info">
              <div className="bio-title2">
                <div className="name-seal-row">
                  <span>Wendy Toh</span>
                  <OptimizedImage
                    src="/Seal.png"
                    alt="Certification Seal"
                    className="certification-seal-inline"
                    width="120"
                    height="78"
                    sizes="120px"
                  />
                </div>
                <span className="bio-certification-title">
                  Certified Personal Financial Wellness Consultant
                </span>
                <span className="bio-contact-detail">
                  Email: OwnYourMoneyNC@gmail.com
                </span>
                <span className="bio-contact-detail">Phone: 919-285-1689</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bio-buttons-row">
          <button className="primary-btn" onClick={toggleContactModal}>
            Schedule a Consultation
          </button>
        </div>
        {isContactModalOpen && <ContactUs onClose={toggleContactModal} />}
      </section>

      <section className="process-section">
        <div className="container-wide">
          <div className="process-section-header">
            <h2>Client-Centered Financial Coaching Process</h2>
            <span>
              A comprehensive approach designed to transform your financial
              future.
            </span>
            <br />
          </div>

          <div className="process-grid">
            <article className="process-card blue">
              <div className="process-header">
                <div className="process-number blue">1</div>
                <h3 className="process-title">Foundation & Discovery</h3>
              </div>
              <ul className="process-list">
                <li>
                  <h4>Clarify Your Financial Vision</h4>
                  <p>Define what financial success looks like for you.</p>
                </li>
                <li>
                  <h4>Share Your Financial Information</h4>
                  <p>Upload relevant documents and data securely.</p>
                </li>
                <li>
                  <h4>Collaborative Coaching Sessions</h4>
                  <p>
                    Meet to review your situation and clarify your values and
                    priorities.
                  </p>
                </li>
              </ul>
            </article>

            <article className="process-card green">
              <div className="process-header">
                <div className="process-number green">2</div>
                <h3 className="process-title">Goal Setting & Assessment</h3>
              </div>
              <ul className="process-list">
                <li>
                  <h4>Establish and Prioritize Your Financial Goals</h4>
                  <p>Align your goals with your vision and timeline.</p>
                </li>
                <li>
                  <h4>Diagnose Key Financial Areas</h4>
                  <p>Identify risks, inefficiencies, and challenges.</p>
                </li>
                <li>
                  <h4>Uncover Knowledge Gaps</h4>
                  <p>
                    Highlight areas where additional financial education will
                    support better decision-making.
                  </p>
                </li>
              </ul>
            </article>

            <article className="process-card purple">
              <div className="process-header">
                <div className="process-number purple">3</div>
                <h3 className="process-title">Planning & Action</h3>
              </div>
              <ul className="process-list">
                <li>
                  <h4>Design a Personalized Financial Action Plan</h4>
                  <p>
                    Build a values-aligned plan with clear success benchmarks
                    and timelines
                  </p>
                </li>
                <li>
                  <h4>Outline Behavioral Shifts Needed</h4>
                  <p>
                    Identify habits or mindsets that may need adjustment for
                    success.
                  </p>
                </li>
                <li>
                  <h4>Document Your Action Steps</h4>
                  <p>
                    Create a step-by-step action plan with timelines and
                    responsibilities.
                  </p>
                </li>
              </ul>
            </article>

            <article className="process-card orange">
              <div className="process-header">
                <div className="process-number orange">4</div>
                <h3 className="process-title">Implementation & Support</h3>
              </div>
              <ul className="process-list">
                <li>
                  <h4>Identify Resources and Tools</h4>
                  <p>
                    Determine what financial products, services, or support
                    systems will aid your plan.
                  </p>
                </li>
                <li>
                  <h4>Set Accountability Structures</h4>
                  <p>
                    Choose check-in methods (texts, emails, calls) that work for
                    you.
                  </p>
                </li>
                <li>
                  <h4>Address Financial Knowledge Gaps</h4>
                  <p>
                    Provide targeted resources and coaching to boost financial
                    literacy.
                  </p>
                </li>
              </ul>
            </article>

            <article className="process-card red">
              <div className="process-header">
                <div className="process-number red">5</div>
                <h3 className="process-title">Ongoing Progress & Adjustment</h3>
              </div>
              <ul className="process-list">
                <li>
                  <h4>Regular Progress Checkpoints</h4>
                  <p>Stay on track through ongoing updates and touchpoints.</p>
                </li>
                <li>
                  <h4>On Demand Support for Questions</h4>
                  <p>Get answers quickly via text or email.</p>
                </li>
                <li>
                  <h4>Review, Reassess, and Refine</h4>
                  <p>
                    Evaluate your progress, adjust the plan, and update your
                    reports regularly.
                  </p>
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
